import React, { useMemo } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles, ThemeProvider, useTheme } from "@material-ui/core/styles";
import {
  AppBar,
  Typography,
  Toolbar,
  Button,
  Avatar,
  IconButton,
} from "@material-ui/core";
import Brightness4OutlinedIcon from "@material-ui/icons/Brightness4Outlined";
import Brightness7OutlinedIcon from "@material-ui/icons/Brightness7Outlined";
import { Actions as LangActions } from "../../../store/ducks/i18n";
import pt from "../../../resources/i18n/pt.svg";
import en from "../../../resources/i18n/en.svg";
import Logo from "../Logo";
import { useLocalStorage } from "../../../common/utils";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import SessionImportButton from "./SessionImportButton";
import SessionHistoryButton from "./SessionHistoryButton";
import SessionExportButton from "./SessionExportButton";
import GeoJsonExportButton from "./GeoJsonExportButton";
import PdfReportButton from "./PdfReportButton";
import ScreenshotButton from "./ScreenshotButton";
import { useFile, usePreProcessing, useResult, useSessionExport, useThemeMode } from "../../contexts/index.jsx";
import { createAppTheme, createShorelineTheme, followsThemeMode } from "../../pages/Shoreline/theme.js";
import { buildSessionImportConfigs } from "./sessionImportConfigs";

const useStyles = makeStyles((theme) => ({
  left: {
    flex: 1,
    display: "flex",
    alignItems: "center",
  },
  title: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
  },
  subtitle: {
    marginLeft: theme.spacing(1.5),
    paddingLeft: theme.spacing(1.5),
    fontSize: "0.75rem",
  },
  flag: {
    width: "20px",
    height: "20px",
    marginLeft: "10px",
    marginRight: "5px",
  },
  logo: {},
  manualButton: {
    marginRight: theme.spacing(2),
  },
}));

const manualLinks = {
  main: {
    "pt-BR": "https://drive.google.com/file/d/1jr5k0Msgvuy-qMryFDxHRLo3_jiDQ2Vd/view?usp=sharing",
    "en-US": "https://drive.google.com/file/d/1wZ-0QSdwRXifyGloouHbo2AjlzTVmUGW/view?usp=sharing",
  },
  acquisition: {
    "pt-BR":
      "https://drive.google.com/file/d/1vBzYyyD5wvLUJiI7EU6_VRItdXkZfF-z/view?usp=sharing",
    "en-US":
      "https://drive.google.com/file/d/1rPTyhF0YxJxooMtfiMRgRx3gt1Y2G2Jv/view?usp=sharing",
  },
  bathymetry: {
    "pt-BR":
      "https://drive.google.com/file/d/1lHzY5KMZjElaLLC20CwGRR_5c14kA0f2/view?usp=sharing",
    "en-US":
      "https://drive.google.com/file/d/1zyw74TB_fIBJL3iSS3JrJkEDJn1hduzp/view?usp=sharing",
  },
  csqueeze: {
    "pt-BR":
      "https://drive.google.com/file/d/14xEpfZf3tKbjJhhSrL0CQ_4GF_I0b6Fl/view?usp=sharing",
    "en-US":
      "https://drive.google.com/file/d/1Q7JsWK355_H9cm4rsVwET62vy1VYJBXn/view?usp=sharing",
  },
  intertidal: {
    "pt-BR":
      "https://drive.google.com/file/d/1gIG0VEC1FAAN6abvTKoKuO-H7nZoWZia/view?usp=sharing",
    "en-US":
      "https://drive.google.com/file/d/1bBTNFnlJ2iy0JC0jI8KeW2AodSIIdKox/view?usp=sharing",
  },
  mepbay: {
    "pt-BR":
      "https://drive.google.com/file/d/1PMyyVhZnSTzCeU-YtfjFIl0VFuk0pCfY/view?usp=sharing ",
    "en-US":
      "https://drive.google.com/file/d/1y5aScjpfnHi_0coSP8xBRoftmA4Vvp6C/view?usp=sharing",
  },
};

const NavigationBar = () => {
  const user = useSelector((state) => state.auth.user, shallowEqual);
  const [language, setLanguage] = useLocalStorage("selectedLanguage", "pt-BR");
  const [t] = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();

  const { setData } = useResult();
  const { setFileData } = useFile();
  const {
    saveProcessingCollectionValues,
    waterMaskMethodRef,
    generateCollection,
    cleanAndMaskImages,
    createMosaicByCommonId,
    collectionRef,
  } = usePreProcessing();

  const { config: exportConfig, geoJsonConfig, pdfConfig } = useSessionExport();
  const { shorelineThemeMode, toggleShorelineThemeMode } = useThemeMode();
  const outerTheme = useTheme();

  const handleLanguageChange = (local) => {
    dispatch(LangActions.setLang(local));
    setLanguage(local);
  };

  const pathname = location?.pathname || "";
  const segments = pathname.split("/").filter(Boolean);

  let manualKey = null;

  if (segments[0] === "main") {
    manualKey = segments[1] || "main";
  }

  const isShorelineResultAlias = pathname === "/main/processing";
  if (isShorelineResultAlias) {
    manualKey = "shoreline";
  }

  const isShoreRResultAlias = pathname === "/main/processing_shore_r";
  if (isShoreRResultAlias) {
    manualKey = "shore_r";
  }

  const isShoreSResultAlias = pathname === "/main/processing_shore_s";
  if (isShoreSResultAlias) {
    manualKey = "shore_s";
  }

  const isResultPage =
    (segments[0] === "main" && segments[2] === "result") ||
    isShorelineResultAlias ||
    isShoreRResultAlias ||
    isShoreSResultAlias;

  const fileLink =
    manualKey && manualLinks[manualKey]
      ? manualLinks[manualKey][language]
      : null;

  const importConfigs = buildSessionImportConfigs({
    dispatch,
    setData,
    setFileData,
    saveProcessingCollectionValues,
    waterMaskMethodRef,
    generateCollection,
    cleanAndMaskImages,
    createMosaicByCommonId,
    collectionRef,
  });

  const importConfig = manualKey && !isResultPage ? importConfigs[manualKey] : null;
  const showExport = isResultPage && exportConfig?.module === manualKey;
  const showGeoJsonExport = isResultPage && geoJsonConfig?.module === manualKey;
  const showPdfReport = isResultPage && pdfConfig?.module === manualKey;

  const THEMED_MODULE_NAMES = {
    shoreline: "forms.shorelineAnalysis.moduleName",
    shore_r: "forms.shoreR.moduleName",
    shore_s: "forms.shoreS.moduleName",
    intertidal: "forms.intertidal.moduleName",
    csqueeze: "forms.csqueeze.moduleName",
    bathymetry: "forms.bathymetry.moduleName",
    euscoast: "forms.euscoast.moduleName",
    mepbay: "forms.mepbay.moduleName",
  };
  const isThemedModule = isResultPage && manualKey in THEMED_MODULE_NAMES;
  const themeToggleAvailable = followsThemeMode(pathname);

  const navTheme = useMemo(() => {
    if (isThemedModule) return createShorelineTheme(shorelineThemeMode, outerTheme);
    return themeToggleAvailable ? createAppTheme(shorelineThemeMode, outerTheme) : outerTheme;
  }, [isThemedModule, themeToggleAvailable, shorelineThemeMode, outerTheme]);

  return (
    <ThemeProvider theme={navTheme}>
    <AppBar
      position="static"
      color="default"
      style={{ backgroundColor: navTheme.palette.background.paper, color: navTheme.palette.text.primary }}>
      <Toolbar variant="dense">
        <div className={classes.left}>
          <Typography className={classes.title} variant="h6" color="inherit">
            <div className={classes.logo}>
              <Logo kind={navTheme.palette.type === "dark" ? "outline" : "filled"} />
            </div>
            {t("self.code")}
          </Typography>
          {isThemedModule && (
            <Typography
              className={classes.subtitle}
              style={{ borderLeft: `1px solid ${navTheme.palette.divider}`, color: navTheme.palette.text.secondary }}
            >
              {t(THEMED_MODULE_NAMES[manualKey])} · {t("forms.shorelineAnalysis.navSubtitle")}
            </Typography>
          )}
        </div>

        {importConfig && <SessionHistoryButton module={importConfig.module} onOpen={importConfig.onImport} />}

        {importConfig && (
          <SessionImportButton module={importConfig.module} onImport={importConfig.onImport} />
        )}

        {showExport && <ScreenshotButton />}

        {showExport && <SessionExportButton onExport={() => exportConfig.exportRef.current()} />}

        {showGeoJsonExport && <GeoJsonExportButton onExport={() => geoJsonConfig.exportRef.current()} />}

        {showPdfReport && <PdfReportButton onExport={() => pdfConfig.exportRef.current()} />}

        {fileLink && (
          <Button
            className={classes.manualButton}
            variant="outlined"
            color="primary"
            href={fileLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("home.instructions.btnManual") || "Manual"}
          </Button>
        )}


        <IconButton
          onClick={toggleShorelineThemeMode}
          disabled={!themeToggleAvailable}
          title={
            themeToggleAvailable
              ? t("forms.shorelineAnalysis.toggleTheme")
              : t("forms.shorelineAnalysis.toggleThemeUnavailable")
          }
        >
          {shorelineThemeMode === "dark" ? <Brightness7OutlinedIcon /> : <Brightness4OutlinedIcon />}
        </IconButton>

        <Button onClick={() => handleLanguageChange("pt-BR")}>
          <Avatar alt="Português" src={pt} className={classes.flag} />
          pt-BR
        </Button>

        <Button onClick={() => handleLanguageChange("en-US")}>
          <Avatar alt="English" src={en} className={classes.flag} />
          en-US
        </Button>
      </Toolbar>
    </AppBar>
    </ThemeProvider>
  );
};

export default NavigationBar;
